/**
 * Ads Campaign Form Hook (mock)
 *
 * Centralized state management for the "Create Campaign" stepper:
 * - one formData object across all steps
 * - step validation
 * - mock saveDraft / submit handlers
 */

import { useCallback, useMemo, useState } from "react";
import type { AdCampaignFormData, AdCampaignStepConfig, AdCampaignValidationErrors } from "../../../../../api/ads/types";
import { createAdCampaign } from "../../../../../api/ads";

type UseAdCampaignFormReturn = {
  functions: {
    goToStep: (stepIndex: number) => void;
    validateStep: (stepIndex: number) => boolean;
    handleFormChange: (updates: Partial<AdCampaignFormData>) => void;
    saveDraft: () => Promise<string>;
    submitCampaign: () => Promise<string>;
  };
  values: {
    currentStep: number;
    steps: AdCampaignStepConfig[];
    formData: AdCampaignFormData;
    validationErrors: AdCampaignValidationErrors;
    isDraft: boolean;
  };
  loading: {
    isSubmitting: boolean;
  };
};

const getTodayLocalISODate = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const useAdCampaignForm = (initialData: AdCampaignFormData): UseAdCampaignFormReturn => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<AdCampaignFormData>(initialData);
  const [validationErrors, setValidationErrors] = useState<AdCampaignValidationErrors>({});
  const [isDraft, setIsDraft] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateStepData = useCallback(
    (stepIndex: number): { ok: boolean; errors: AdCampaignValidationErrors } => {
      const errors: AdCampaignValidationErrors = {};
      const today = getTodayLocalISODate();

      const start = formData.startDate;
      const end = formData.endDate;

      const isEmpty = (v: unknown) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

      if (stepIndex === 0) {
        if (isEmpty(formData.advertiserId)) errors.advertiserId = "Advertiser is required";
        if (isEmpty(formData.campaignName)) errors.campaignName = "Campaign name is required";
        if (isEmpty(formData.pricingModel) || formData.pricingModel === "") errors.pricingModel = "Pricing model is required";
        if (isEmpty(formData.pricingRate) || Number(formData.pricingRate) <= 0) errors.pricingRate = "Pricing rate is required";
        if (isEmpty(formData.dailyBudget) || Number(formData.dailyBudget) <= 0) errors.dailyBudget = "Daily budget is required";
        if (isEmpty(formData.totalBudget) || Number(formData.totalBudget) <= 0) errors.totalBudget = "Total budget is required";

        if (isEmpty(start)) errors.startDate = "Start date is required";
        if (isEmpty(end)) errors.endDate = "End date is required";

        if (start && start < today) errors.startDate = "Start date cannot be in the past";
        if (end && end < today) errors.endDate = "End date cannot be in the past";

        if (start && end && end < start) errors.endDate = "End date must be after start date";
      }

      if (stepIndex === 1) {
        if (isEmpty(formData.placementId)) errors.placementId = "Placement is required";
        if (isEmpty(formData.ctaText)) errors.ctaText = "CTA text is required";
        if (isEmpty(formData.landingUrl)) errors.landingUrl = "Landing URL is required";

        // Mock: we accept creative file as optional, but if provided it must be a File
        if (formData.creativeFile && !(formData.creativeFile instanceof File)) {
          errors.creativeFile = "Invalid creative file";
        }
      }

      if (stepIndex === 2) {
        if (!formData.countries || formData.countries.length === 0) errors.countries = "Select at least one country";
      }

      if (stepIndex === 3) {
        // Summary validation re-checks prior steps
        const step0 = validateStepDataForIndex0();
        const step1 = validateStepDataForIndex1();
        const step2 = validateStepDataForIndex2();
        const mergedErrors: AdCampaignValidationErrors = { ...step0.errors, ...step1.errors, ...step2.errors };
        return { ok: !Object.keys(mergedErrors).length, errors: mergedErrors };
      }

      return { ok: !Object.keys(errors).length, errors };

      function validateStepDataForIndex0(): { ok: boolean; errors: AdCampaignValidationErrors } {
        const e: AdCampaignValidationErrors = {};
        if (isEmpty(formData.advertiserId)) e.advertiserId = "Advertiser is required";
        if (isEmpty(formData.campaignName)) e.campaignName = "Campaign name is required";
        if (isEmpty(formData.pricingModel) || formData.pricingModel === "") e.pricingModel = "Pricing model is required";
        if (isEmpty(formData.pricingRate) || Number(formData.pricingRate) <= 0) e.pricingRate = "Pricing rate is required";
        if (isEmpty(formData.dailyBudget) || Number(formData.dailyBudget) <= 0) e.dailyBudget = "Daily budget is required";
        if (isEmpty(formData.totalBudget) || Number(formData.totalBudget) <= 0) e.totalBudget = "Total budget is required";
        if (isEmpty(start)) e.startDate = "Start date is required";
        if (isEmpty(end)) e.endDate = "End date is required";
        if (start && start < today) e.startDate = "Start date cannot be in the past";
        if (end && end < today) e.endDate = "End date cannot be in the past";
        if (start && end && end < start) e.endDate = "End date must be after start date";
        return { ok: !Object.keys(e).length, errors: e };
      }

      function validateStepDataForIndex1(): { ok: boolean; errors: AdCampaignValidationErrors } {
        const e: AdCampaignValidationErrors = {};
        if (isEmpty(formData.placementId)) e.placementId = "Placement is required";
        if (isEmpty(formData.ctaText)) e.ctaText = "CTA text is required";
        if (isEmpty(formData.landingUrl)) e.landingUrl = "Landing URL is required";
        if (formData.creativeFile && !(formData.creativeFile instanceof File)) {
          e.creativeFile = "Invalid creative file";
        }
        return { ok: !Object.keys(e).length, errors: e };
      }

      function validateStepDataForIndex2(): { ok: boolean; errors: AdCampaignValidationErrors } {
        const e: AdCampaignValidationErrors = {};
        if (!formData.countries || formData.countries.length === 0) e.countries = "Select at least one country";
        return { ok: !Object.keys(e).length, errors: e };
      }
    },
    [formData]
  );

  const steps: AdCampaignStepConfig[] = useMemo(() => {
    return [
      { id: "basic", title: "Basic Information", displayStepNumber: 1, isCompleted: false },
    ];
  }, []);

  const goToStep = useCallback((stepIndex: number) => {
    setCurrentStep(stepIndex);
  }, []);

  const validateStep = useCallback(
    (stepIndex: number) => {
      const { ok, errors } = validateStepData(stepIndex);
      setValidationErrors(errors);
      return ok;
    },
    [validateStepData]
  );

  const handleFormChange = useCallback((updates: Partial<AdCampaignFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));

    // Clear touched errors for updated keys.
    if (Object.keys(updates).length) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(updates) as (keyof AdCampaignFormData)[]) {
          if (key in next) delete (next as any)[key];
        }
        return next;
      });
    }
  }, []);

  const saveDraft = useCallback(async (): Promise<string> => {
    setIsSubmitting(true);
    try {
      setIsDraft(true);
      const { ok, errors } = validateStepData(0);
      if (!ok) {
        setValidationErrors(errors);
        throw new Error("Please fix the highlighted fields before saving.");
      }
      const result = await createAdCampaign(formData);
      return result.message;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateStepData]);

  const submitCampaign = useCallback(async (): Promise<string> => {
    setIsSubmitting(true);
    try {
      setIsDraft(false);
      const { ok, errors } = validateStepData(0);
      if (!ok) {
        setValidationErrors(errors);
        throw new Error("Please fix the highlighted fields before submitting.");
      }
      const result = await createAdCampaign(formData);
      return result.message;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateStepData]);

  // Ensure hook return matches your required structure.
  return {
    functions: {
      goToStep,
      validateStep,
      handleFormChange,
      saveDraft,
      submitCampaign,
    },
    values: {
      currentStep,
      steps,
      formData,
      validationErrors,
      isDraft,
    },
    loading: {
      isSubmitting,
    },
  };
};

